/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/B2BEventParticipatingCompanyView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import LanguageSelector from "../LanguageSelector/index.jsx";
import NotificationBell from "../NotificationBell/index.jsx";
import B2BEventParticipatingCompanyForm from "../B2BEventParticipatingCompanyForm/index.jsx";
import { useTranslation } from "react-i18next";

import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";

import styles from "./B2BEventParticipatingCompanyView.module.css";

/**
 * Full-page add/edit participating company for tablet and mobile (mirrors the desktop modal).
 * Top row: go back (left), notification bell and language selector (right).
 *
 * @param {Object} props
 * @param {import("react-router").NavigateFunction} props.navigate - For {@link NotificationBell}.
 * @param {function} props.onBack - Closes this view (same as cancel).
 * @param {function} props.onSuccess - Passed through to the form.
 * @param {function} props.onCancel - Usually same as onBack; used by form cancel button.
 */
const B2BEventParticipatingCompanyView = ({
  navigate,
  onBack,
  onCancel,
  onSuccess,
  ...formProps
}) => {
  const { t } = useTranslation();
  const handleCancel = onCancel ?? onBack;

  return (
    <div className={styles.viewRoot}>
      <div className={styles.topBar}>
        <div className={styles.topBarRow}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            aria-label={t("common.goBack")}
          >
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
          <div className={styles.topBarRight}>
            <NotificationBell navigate={navigate} />
            <LanguageSelector />
          </div>
        </div>
      </div>
      <div className={styles.formScroll}>
        <B2BEventParticipatingCompanyForm
          active
          variant="view"
          onCancel={handleCancel}
          onSuccess={onSuccess}
          {...formProps}
        />
      </div>
    </div>
  );
};

export default B2BEventParticipatingCompanyView;
